import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppConfiguration,
  AppConfigurationContextState,
  AppError,
} from './appConfigurationContext.types';
import { BookingDataContext } from '../bookingDataContext/bookingDataContext';
import { BookingDataStatus } from '../../components/BookingWidget/BookingManagment/bookingManagment.interface';
import { NotificationContext } from '../../shared/components/Notification/NotificationContext';
import authHelper from '../../shared/helpers/auth.helper';
import { CacheDataContext } from '../cacheContext/cacheContext';

const contextDefaultValues: AppConfigurationContextState = {
  appConfiguration: null,
  updateConfiguration: (appConfiguration: AppConfiguration): void => undefined,
  isPaymentHome: false,
  appError: null,
  setAppError: (error: AppError | null) => undefined,
};
export const AppConfigurationContext =
  createContext<AppConfigurationContextState>(contextDefaultValues);

export const AppConfigurationContextProvider: React.FC<
  React.PropsWithChildren<object>
> = ({ children }) => {
  const { t } = useTranslation();
  const { bookingDataResponse } = useContext(BookingDataContext);
  const { showNotification } = useContext(NotificationContext);
  const { getUrlParam } = useContext(CacheDataContext);
  const [isPaymentHome, setIsPaymentHome] = useState(false);
  const [appError, setAppError] = useState<AppError | null>(null);
  const { isAgent } = authHelper();
  const [appConfiguration, setAppConfig] = useState<AppConfiguration | null>(
    contextDefaultValues.appConfiguration,
  );

  useEffect(() => {
    if (
      getUrlParam('paymentHome') !== 'true' ||
      !bookingDataResponse ||
      isAgent()
    ) {
      return;
    }

    if (
      bookingDataResponse?.bookingData.status !==
      BookingDataStatus.PENDING_PAYMENT
    ) {
      showNotification('paymentPendingRequired', 'error', false);
      setAppError({
        error: t('NOTIFICATION.ERROR.paymentPendingRequired'),
        description: t('NOTIFICATION.ERROR.bookingInInvalidStatus', {
          expectedStatus: BookingDataStatus.PENDING_PAYMENT,
          actualStatus: bookingDataResponse?.bookingData.status ?? 'unknown',
        }),
        traceStack: new Error(),
      });
      return;
    }

    if (!getUrlParam('secret')) {
      showNotification('paymentSecretRequired', 'error', false);
      setAppError({
        error: t('NOTIFICATION.ERROR.paymentSecretRequired'),
        description: t('NOTIFICATION.ERROR.paymentSecretRequired'),
        traceStack: new Error(),
      });
      return;
    }

    setIsPaymentHome(true);
  }, [bookingDataResponse, showNotification]);

  const handleUpdate = (appConfigurationData: AppConfiguration): void => {
    setAppConfig(appConfigurationData);
  };

  const appConfig: AppConfigurationContextState = useMemo(
    () => ({
      appConfiguration,
      updateConfiguration: handleUpdate,
      isPaymentHome,
      appError,
      setAppError,
    }),
    [appConfiguration, isPaymentHome, appError],
  );

  return (
    <AppConfigurationContext.Provider value={appConfig}>
      {children}
    </AppConfigurationContext.Provider>
  );
};
